import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Select, type SelectOption } from "@/components/ui/select"
import { COLUMNS, type ColumnKey } from "./columns"
import { cn } from "@/lib/cn"

type Props = {
  query: string
  onQueryChange: (q: string) => void
  sortBy: string | null
  sortDir: "asc" | "desc"
  onSortChange: (sortBy: string | null, dir: "asc" | "desc") => void
  visibleColumns: ColumnKey[]
  onVisibleColumnsChange: (cols: ColumnKey[]) => void
}

// Single + compound sort options. The compound `priority_sla` /
// `sla_priority` values are server-side presets (see
// SORT_PRESETS in tickets_controller.rb). The direction toggle flips
// the whole ordering — for compound sorts that means both axes invert.
const SORT_OPTIONS: SelectOption<string>[] = [
  { value: "",                label: "Default (Priority + SLA + Escalated + Created)" },
  { value: "priority_sla",    label: "Priority + SLA" },
  { value: "sla_priority",    label: "SLA + Priority" },
  { value: "created_at",      label: "Created at" },
  { value: "escalated_at",    label: "Escalated at" },
  { value: "last_message_at", label: "Last response" },
  { value: "priority",        label: "Priority" },
  { value: "state",           label: "Status" },
]

export function TicketsToolbar({
  query,
  onQueryChange,
  sortBy,
  sortDir,
  onSortChange,
  visibleColumns,
  onVisibleColumnsChange,
}: Props) {
  const [local, setLocal] = useState(query)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [columnsOpen, setColumnsOpen] = useState(false)
  const columnsRef = useRef<HTMLDivElement | null>(null)

  // Reset local mirror when the controlling URL param changes (e.g.
  // "Clear" button on the filter bar).
  useEffect(() => { setLocal(query) }, [query])

  // 250ms debounce → server. Cancel pending if input changes again.
  useEffect(() => {
    if (local === query) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => onQueryChange(local), 250)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [local, query, onQueryChange])

  // `/` global shortcut to focus search; Esc clears + refocuses.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase()
      const editing = tag === "input" || tag === "textarea" || (e.target as HTMLElement | null)?.isContentEditable
      if (e.key === "/" && !editing) {
        e.preventDefault()
        inputRef.current?.focus()
      } else if (e.key === "Escape" && document.activeElement === inputRef.current) {
        setLocal("")
        onQueryChange("")
      }
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [onQueryChange])

  // Click-outside closes the columns dropdown.
  useEffect(() => {
    if (!columnsOpen) return
    function handler(e: MouseEvent) {
      if (columnsRef.current && !columnsRef.current.contains(e.target as Node)) {
        setColumnsOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [columnsOpen])

  function toggleColumn(key: ColumnKey) {
    const has = visibleColumns.includes(key)
    if (has) {
      // Don't let the user hide every column — keep at least one.
      if (visibleColumns.length <= 1) return
      onVisibleColumnsChange(visibleColumns.filter((k) => k !== key))
    } else {
      // Preserve canonical order from COLUMNS.
      const next = COLUMNS.map((c) => c.key).filter((k) => k === key || visibleColumns.includes(k))
      onVisibleColumnsChange(next)
    }
  }

  const selectedSort = SORT_OPTIONS.find((o) => o.value === (sortBy ?? "")) ?? SORT_OPTIONS[0]

  return (
    <div className="flex flex-col gap-3 rounded-card border border-border bg-app-panel p-3 sm:flex-row sm:flex-wrap sm:items-center">
      {/* Search input. Full-width on mobile (sm:flex-1 in the row);
          the `/` shortcut chip hides below sm: where there's no
          physical keyboard. */}
      <div className="relative w-full sm:min-w-[260px] sm:flex-1">
        <input
          ref={inputRef}
          type="search"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder="Search by user ID, name, email, ticket ID, or subject…"
          className={cn(
            "w-full min-h-11 rounded-xl border border-border bg-white/5 px-9 text-sm text-app-text",
            "placeholder:text-app-subtle outline-none transition focus:border-brand-red/50 focus:shadow-[0_0_0_3px_var(--brand-red-soft)]",
          )}
        />
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-app-subtle" aria-hidden>
          🔍
        </span>
        <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 hidden rounded-control border border-border bg-white/5 px-1.5 py-0.5 text-[10px] text-app-subtle sm:block">/</kbd>
      </div>

      {/* Sort row — label stacks above the select on the narrowest
          phones (the "Sort by" label is preserved as a separate row);
          select + direction toggle stay inline. Min-h-11 keeps the
          direction button at the WCAG touch-target minimum. */}
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-app-muted">Sort</span>
        <div className="min-w-[160px] flex-1 sm:flex-none">
          <Select
            value={selectedSort}
            options={SORT_OPTIONS}
            onChange={(o) => onSortChange((o?.value ?? "") || null, sortDir)}
            isClearable={false}
          />
        </div>
        <button
          type="button"
          aria-label={`Sort ${sortDir === "asc" ? "ascending" : "descending"}`}
          onClick={() => onSortChange(sortBy, sortDir === "asc" ? "desc" : "asc")}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-brand-red/40 bg-brand-redSoft text-brand-red transition hover:bg-brand-red/20"
        >
          {sortDir === "asc" ? "↑" : "↓"}
        </button>
      </div>

      {/* Columns picker — hidden below sm: (the mobile card layout has
          a fixed compact field set; column toggling is a desktop tool). */}
      <div className="relative hidden sm:block" ref={columnsRef}>
        <Button variant="ghost" size="md" onClick={() => setColumnsOpen((o) => !o)}>
          Columns
        </Button>
        {columnsOpen && (
          <div className="absolute right-0 top-full z-30 mt-2 w-56 rounded-card border border-border bg-app-panel2 p-2 shadow-xl">
            {COLUMNS.map((c) => {
              const checked = visibleColumns.includes(c.key)
              return (
                <label
                  key={c.key}
                  className="flex cursor-pointer items-center gap-2 rounded-control px-2 py-1.5 text-sm text-app-text hover:bg-white/5"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleColumn(c.key)}
                    className="accent-brand-red"
                  />
                  <span>{c.label}</span>
                </label>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
